<script setup>
import XlSlowNotice from '../xl/XlSlowNotice.vue';
import HubSessionRoom from './HubSessionRoom.vue';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useBreakpoint } from '../../composables/useBreakpoint';
import HubSidebar from './HubSidebar.vue';
import HubTopBar from './HubTopBar.vue';

/**
 * The frame every Hub page renders inside.
 *
 * Keeps the `admin-container` class deliberately: every page's gutters live in
 * `.admin-container .page-content` and friends in style.scss, so dropping the
 * class would silently strip the padding off every page in the Hub. The class
 * is a style contract now, not a layout one.
 *
 * Three widths, one layout: a full sidebar, an icon rail, and — on a phone —
 * an overlay drawer. The width itself lives in --hub-sidebar-width (see
 * style.scss) so that anything needing to reason about it, like the five
 * agenda modals, reads one value instead of five copies of `316px`.
 */
const STORAGE_KEY = 'hub.sidebar.collapsed';

const isNarrow = useBreakpoint('(max-width: 900px)');
const isMedium = useBreakpoint('(max-width: 1279.98px)');

// The manual preference persists: the old sidebar reset to expanded on every
// reload, so anyone who preferred the rail re-collapsed it all day.
const manuallyCollapsed = ref(false);
const drawerOpen = ref(false);

onMounted(() => {
  try {
    manuallyCollapsed.value = window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch (e) {
    // Private browsing, or storage disabled. Defaults are fine.
  }
});

// Below the medium breakpoint the viewport decides and the preference is
// ignored — not overwritten, so it comes back on a wide screen.
const collapsed = computed(() => !isNarrow.value && (isMedium.value || manuallyCollapsed.value));

const toggle = () => {
  manuallyCollapsed.value = !manuallyCollapsed.value;
  try {
    window.localStorage.setItem(STORAGE_KEY, String(manuallyCollapsed.value));
  } catch (e) {
    // Not worth failing a click over.
  }
};

const closeDrawer = () => {
  drawerOpen.value = false;
};

// A drawer left open behind a new page is a trap on a small screen.
watch(isNarrow, (narrow) => {
  if (!narrow) drawerOpen.value = false;
});

const onKeydown = (event) => {
  if (event.key === 'Escape' && drawerOpen.value) closeDrawer();
};

onMounted(() => document.addEventListener('keydown', onKeydown));
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));
</script>

<template>
  <div
    class="hub-shell admin-container"
    :class="{ 'hub-shell--collapsed': collapsed, 'hub-shell--drawer': isNarrow }"
  >
    <div class="hub-shell__sidebar" data-xl-gen="northstar" :class="{ 'hub-shell__sidebar--open': drawerOpen }">
      <HubSidebar
        :collapsed="collapsed && !drawerOpen"
        :drawer="isNarrow"
        @toggle="toggle"
        @close="closeDrawer"
        @navigate="closeDrawer"
      />
    </div>

    <div v-if="isNarrow && drawerOpen" class="hub-shell__scrim" @click="closeDrawer" />

    <div class="hub-shell__body">
      <HubTopBar :show-menu-button="isNarrow" @open-nav="drawerOpen = true" />
      <!-- The page's area: it fades in as a page arrives; the bars around
           it are the shell and do not. -->
      <main class="hub-shell__main" data-page-slot>
        <XlSlowNotice />
        <slot />
      </main>
    </div>

    <!-- A session's room, opened over whichever hub page asked for it: staff
         run sessions in the hub, never in the policyholder portal. -->
    <HubSessionRoom />
  </div>
</template>

<style scoped>
.hub-shell {
  display: flex;
  min-height: 100vh;
  /* The prototype's paper (#FCFAFD), not the app-wide grey this used to be. */
  background: #fcfafd;
}

/* Collapsing must narrow the sidebar, not just re-lay-out its items. Without
   this the rail styling was applied inside a still-316px column, which is a
   wide empty panel with small centred icons in it. */
.hub-shell--collapsed {
  --hub-sidebar-width: var(--hub-sidebar-rail);
}

.hub-shell__sidebar {
  position: sticky;
  top: 0;
  z-index: 52;
  width: var(--hub-sidebar-width);
  height: 100vh;
  flex-shrink: 0;
  /* Pulls the page's grey under the sidebar's rounded right corner. The top
     bar compensates for the same 10px — see HubTopBar. */
  margin-right: -10px;
  transition: width 250ms cubic-bezier(0.2, 0, 0, 1);
}

/* On a phone the sidebar leaves the layout entirely and slides over the page,
   so the content gets the full width instead of being squeezed. */
.hub-shell--drawer .hub-shell__sidebar {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 61;
  width: 280px;
  margin-right: 0;
  transform: translateX(-100%);
  transition: transform 250ms cubic-bezier(0.2, 0, 0, 1);
}

.hub-shell--drawer .hub-shell__sidebar--open {
  transform: translateX(0);
}

.hub-shell__scrim {
  position: fixed;
  inset: 0;
  z-index: 60;
  background: rgba(0, 0, 0, 0.32);
}

.hub-shell__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.hub-shell__main {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

@media (prefers-reduced-motion: reduce) {
  .hub-shell__sidebar {
    transition: none;
  }
}
</style>
